import React from 'react';
import glamorous from 'glamorous';
import Feature1 from '../shared/icons/key-features/teams_icon.svg';
import Feature2 from '../shared/icons/key-features/team-profiles.svg';
import Feature3 from '../shared/icons/key-features/automated-creation.svg';
import Feature4 from '../shared/icons/key-features/sso.svg';
import Feature5 from '../shared/icons/key-features/encryption.svg';
import Feature6 from '../shared/icons/key-features/multi-tenant.svg';
import Feature7 from '../shared/icons/key-features/ldap.svg';
import Feature8 from '../shared/icons/key-features/github.svg';
import Feature9 from '../shared/icons/key-features/on-premises.svg';
import Feature10 from '../shared/icons/key-features/aicpa.svg';
import {WHITE, CHARCOAL} from '../../../shared/style/colors';
import {PHONE} from '../../../shared/style/breakpoints';
import {MAX_PAGE_WIDTH} from '../../../shared/style/dimensions';
import {WEIGHT, FONTS} from '../../../shared/style/typography';

const Container = glamorous.div({
  width: '100%',
  background: CHARCOAL,
  textAlign: 'center'
});

const SubContainer = glamorous.div({
  width: '100%',
  maxWidth: MAX_PAGE_WIDTH,
  display: 'inline-flex',
  alignItems: 'center',
  flexDirection: 'column',
  textAlign: 'center',
  marginBottom: 95,
  [PHONE]: {
    width: '85%',
    marginBottom: 50
  }
});

const Title = glamorous.h3({
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontSize: 44,
  letterSpacing: 0.12,
  lineHeight: '53px',
  fontWeight: WEIGHT.ULTRA_BLACK,
  color: WHITE,
  marginTop: 103,
  marginBottom: 56,
  [PHONE]: {
    fontSize: 28,
    lineHeight: '35px'
  }
});

const Features = glamorous.ul({
  gridTemplateColumns: 'repeat(auto-fill, minmax(195px, auto))',
  gap: '45px 90px',
  padding: 0,
  display: 'grid',
  width: '100%',
  maxWidth: 880,
  [PHONE]: {
    gridColumnGap: 30,
    gridRowGap: 48,
    gridTemplateColumns: '1fr',
    padding: 0
  }
});

const FeatureContainer = glamorous.li(
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    listStyle: 'none'
  },
  ({extraStyle}) => (extraStyle ? extraStyle : {})
);

const FeatureTitle = glamorous.div({
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontSize: 20,
  fontWeight: WEIGHT.NORMAL,
  lineHeight: 1.67,
  letterSpacing: 0.06,
  textAlign: 'center',
  color: WHITE,
  marginTop: 13
});

const FeatureComingSoon = glamorous.div({
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  opacity: 0.5,
  fontSize: 18,
  fontWeight: WEIGHT.NORMAL,
  lineHeight: 1.07,
  letterSpacing: 0.06,
  textAlign: 'center',
  color: WHITE
});

const FeatureIcon = glamorous.div({
  height: 72,
  width: 72,
  background: WHITE,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '> a': {
    backgroundColor: CHARCOAL,
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: CHARCOAL
    }
  }
});

const Notice = glamorous.p({
  fontFamily: `${FONTS.CEREBRI_SANS_PRO} !important`,
  fontSize: 18,
  fontWeight: WEIGHT.NORMAL,
  lineHeight: '34px',
  textAlign: 'center',
  color: WHITE,
  opacity: 0.7,
  marginTop: 92,
  [PHONE]: {
    marginTop: 50
  }
});

const Contact = glamorous.a({
  color: WHITE,
  '&:hover': {
    color: WHITE,
    textDecoration: 'underline'
  }
});
const featuresForPrivateStackshare = [
  {
    icon: Feature1,
    title: 'Full community access',
    comingSoon: false
  },
  {
    icon: Feature2,
    title: 'Team profiles with permissions management',
    comingSoon: false
  },
  {
    icon: Feature3,
    title: 'Automated creation and updating of stack data',
    comingSoon: false
  },
  {
    icon: Feature4,
    title: 'SSO via Okta & AWS Cognito',
    comingSoon: false
  },
  {
    icon: Feature5,
    title: 'Data encryption at rest',
    comingSoon: false
  },
  {
    icon: Feature6,
    title: 'Multi-tenant hosted on stackshare.io',
    comingSoon: false
  },
  {
    icon: Feature7,
    title: 'LDAP/Active Directory Sync',
    comingSoon: false
  },
  {
    icon: Feature8,
    title: 'Integration with GitHub, Azure DevOps, and Bitbucket',
    comingSoon: true,
    whichItemComingSoon: 'Gitlab'
  },
  {
    icon: Feature9,
    title: 'On-premises',
    comingSoon: true,
    whichItemComingSoon: ''
  },
  {
    icon: Feature10,
    title: 'SOC 2 Type 1 Certified',
    comingSoon: true,
    whichItemComingSoon: 'Type 2',
    extraStyle: {gridColumn: 'span 3', [PHONE]: {gridColumn: 'span 1'}},
    isLinkable: true,
    link: 'http://www.aicpa.org/soc4so'
  }
];

const PrivateTeamFeatures = () => {
  return (
    <Container>
      <SubContainer>
        <Title>Key features and integrations</Title>
        <Features>
          {featuresForPrivateStackshare.map((item, index) => (
            <FeatureContainer key={index} extraStyle={item.extraStyle}>
              <FeatureIcon>
                {item.isLinkable ? (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="nofollow noreferrer noopener"
                    title={item.title}
                  >
                    <item.icon />
                  </a>
                ) : (
                  <item.icon />
                )}
              </FeatureIcon>
              <FeatureTitle>{item.title}</FeatureTitle>
              {item.comingSoon && (
                <FeatureComingSoon>
                  {item.whichItemComingSoon
                    ? `(${item.whichItemComingSoon} Coming Soon)`
                    : '(Coming Soon)'}
                </FeatureComingSoon>
              )}
            </FeatureContainer>
          ))}
        </Features>
        <Notice>
          {`*StackShare doesn't store any of your source code on our servers; we temporarily read
          config files and repo metadata contained in each repo to detect the tech stack and then
          store the results, which only you have access to.`}{' '}
          <br />
          <Contact href="mailto:api@stackshare.io" title="Contact us to learn more.">
            Contact us to learn more
          </Contact>{' '}
        </Notice>
      </SubContainer>
    </Container>
  );
};

export default PrivateTeamFeatures;
